import React from 'react';
import {
	useSelector,
} from 'react-redux';
import _throttle from 'lodash/throttle';
import {
	isWebsiteSwitcherOpenSelector,
} from '~/state/ui/menu/selectors';

/**
 * Hook to position the websites dropdown relative to WebsitesNavToggle in new navigation
 *
 * This hook handles dynamic positioning of the dropdown when it's nested inside
 * the Technical section's expandable menu in the new navigation layout.
 *
 * @param dropdownTargetRef - Ref to the dropdown container element
 * @param isNewNavigation - Whether new navigation is active
 */
export function useWebsitesDropdownPosition(
	dropdownTargetRef: React.RefObject<HTMLDivElement | null>,
	isNewNavigation: boolean,
): void {
	const isWebsiteSwitcherOpen = useSelector(isWebsiteSwitcherOpenSelector);

	// Use useLayoutEffect to hide dropdown immediately before paint
	// This prevents the dropdown from briefly appearing at the wrong position
	React.useLayoutEffect(() => {
		if (!isNewNavigation) {
			// Clean up CSS custom properties AND inline styles when switching away from new navigation
			// This ensures old navigation dropdown works correctly
			if (dropdownTargetRef.current) {
				// Remove CSS custom properties
				dropdownTargetRef.current.style.removeProperty('--dropdown-top');
				dropdownTargetRef.current.style.removeProperty('--dropdown-left');
				dropdownTargetRef.current.style.removeProperty('--dropdown-width');

				// Remove inline styles that were set with !important for new navigation
				// These need to be cleared so old navigation's relative positioning works
				dropdownTargetRef.current.style.removeProperty('top');
				dropdownTargetRef.current.style.removeProperty('left');
				dropdownTargetRef.current.style.removeProperty('width');
				dropdownTargetRef.current.style.removeProperty('position');
				dropdownTargetRef.current.style.removeProperty('max-width');
				dropdownTargetRef.current.style.removeProperty('right');
			}

			return;
		}

		// Initialize dropdown as hidden until positioned (synchronous, before paint)
		if (dropdownTargetRef.current) {
			dropdownTargetRef.current.style.setProperty('--dropdown-top', '-9999px');
			dropdownTargetRef.current.style.setProperty('--dropdown-left', '0');
			dropdownTargetRef.current.style.setProperty('--dropdown-width', 'auto');
			// Also set inline style directly to ensure it's hidden
			dropdownTargetRef.current.style.setProperty('top', '-9999px', 'important');
			dropdownTargetRef.current.style.setProperty('position', 'fixed', 'important');
		}
	}, [isNewNavigation, dropdownTargetRef]);

	// Use useEffect for positioning calculations (after DOM is ready)
	React.useEffect(() => {
		// Copy ref value at start of effect to avoid stale closure
		const dropdownTarget = dropdownTargetRef.current;

		// Only apply positioning logic for new navigation
		if (!isNewNavigation) {
			// Clean up inline styles when switching back to old navigation
			// This ensures old navigation's relative positioning works correctly
			if (dropdownTarget) {
				dropdownTarget.style.removeProperty('top');
				dropdownTarget.style.removeProperty('left');
				dropdownTarget.style.removeProperty('width');
				dropdownTarget.style.removeProperty('position');
				dropdownTarget.style.removeProperty('max-width');
				dropdownTarget.style.removeProperty('right');
			}

			return;
		}

		// Find the toggle container once for ResizeObserver
		const sidebar = dropdownTarget?.closest('.sidebar');
		const toggleContainer = sidebar?.querySelector('[data-websites-nav-toggle]') as HTMLElement | null;

		const updatePosition = () => {
			if (!dropdownTarget) {
				return;
			}

			// If dropdown is not open, hide it
			if (!isWebsiteSwitcherOpen) {
				dropdownTarget.style.setProperty('--dropdown-top', '-9999px');
				dropdownTarget.style.setProperty('top', '-9999px', 'important');
				return;
			}

			// Find the WebsitesNavToggle container (li element) inside Technical section
			const currentSidebar = dropdownTarget.closest('.sidebar');
			if (!currentSidebar) {
				return;
			}

			const currentToggleContainer = currentSidebar.querySelector('[data-websites-nav-toggle]') as HTMLElement;
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			if (!currentToggleContainer) {
				return;
			}

			// Find the actual Website component - this is the visible clickable element
			const websiteElement = currentToggleContainer.querySelector('.website') as HTMLElement;

			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			if (!websiteElement) {
				return;
			}

			// Get measurements for positioning
			const containerRect = currentToggleContainer.getBoundingClientRect();
			const websiteRect = websiteElement.getBoundingClientRect();

			// Check if element is visible (Technical section is expanded)
			if (websiteRect.width > 0 && websiteRect.height > 0) {
				// Calculate position: use container's bottom edge
				const calculatedTop = containerRect.bottom;

				// Find a reference navbar item (like "Home" or "Content") to get the correct alignment
				// We need to align with where the main navbar items' content starts, not sub-items
				// Exclude items inside expandable sections (sub-items) and dividers
				const mainNavItems = currentSidebar.querySelectorAll('.main-nav__list > .main-nav__item:not(.main-nav__item--expandable)');
				let referenceItem: HTMLElement | null = null;
				let referenceMenuItemLayout: HTMLElement | null = null;

				// Find the first main navbar item that has a menu-item-layout (like Home, Content, etc.)
				// Skip expandable items (like Technical) and items without menu-item-layout
				for (const item of Array.from(mainNavItems) as Array<HTMLElement>) {
					// Skip if it's an expandable item or divider
					if (item.classList.contains('main-nav__item--expandable') || item.classList.contains('main-nav__divider')) {
						continue;
					}

					const menuItemLayout = item.querySelector('.menu-item-layout') as HTMLElement;
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
					if (menuItemLayout) {
						referenceItem = item;
						referenceMenuItemLayout = menuItemLayout;
						break;
					}
				}

				// If we found a reference item, use it for alignment

				if (referenceItem && referenceMenuItemLayout) {
					const referenceLayoutRect = referenceMenuItemLayout.getBoundingClientRect();

					// Get computed styles to account for padding
					const referenceLayoutStyle = window.getComputedStyle(referenceMenuItemLayout);
					const referenceLayoutPaddingLeft = parseFloat(referenceLayoutStyle.paddingLeft) || 0;
					const referenceLayoutPaddingRight = parseFloat(referenceLayoutStyle.paddingRight) || 0;

					// Align dropdown with navbar items' CONTENT start position (not element edge)
					// menu-item-layout has: margin 8px + padding 8px
					// So content starts at: element.left + padding-left
					// This matches where the actual text/icons start inside menu-item-layout
					const calculatedLeft = referenceLayoutRect.left + referenceLayoutPaddingLeft;

					// Calculate width: match the content width of navbar items
					// Content width = element width - (padding left + padding right)
					const calculatedWidth = referenceLayoutRect.width - referenceLayoutPaddingLeft - referenceLayoutPaddingRight;

					// Use CSS custom properties for CSS-based positioning
					// Also set inline styles directly as fallback to ensure positioning works
					dropdownTarget.style.setProperty('--dropdown-top', `${calculatedTop}px`);
					dropdownTarget.style.setProperty('--dropdown-left', `${calculatedLeft}px`);
					dropdownTarget.style.setProperty('--dropdown-width', `${calculatedWidth}px`);

					// Set inline styles directly as well (for immediate application)
					// This ensures positioning works even if CSS variable isn't read correctly
					dropdownTarget.style.setProperty('top', `${calculatedTop}px`, 'important');
					dropdownTarget.style.setProperty('left', `${calculatedLeft}px`, 'important');
					dropdownTarget.style.setProperty('width', `${calculatedWidth}px`, 'important');
					dropdownTarget.style.setProperty('position', 'fixed', 'important');
				} else {
					// Fallback: use website element calculations if reference not found
					const computedStyle = window.getComputedStyle(websiteElement);
					const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
					const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
					const marginRight = parseFloat(computedStyle.marginRight) || 0;
					const paddingRight = parseFloat(computedStyle.paddingRight) || 0;

					// Position dropdown to align with navbar items' content (after margin + padding)
					const calculatedLeft = containerRect.left + marginLeft + paddingLeft;

					// Calculate width: match the content width of navbar items
					// Content width = container width - (margin + padding on both sides)
					// Ensure width is never 0 or negative (fallback to container width if calculation fails)
					const calculatedWidth = Math.max(
						containerRect.width - (marginLeft + paddingLeft + marginRight + paddingRight),
						containerRect.width * 0.5 // Minimum 50% of container width as fallback
					);

					// Use CSS custom properties for CSS-based positioning
					// Also set inline styles directly as fallback to ensure positioning works
					dropdownTarget.style.setProperty('--dropdown-top', `${calculatedTop}px`);
					dropdownTarget.style.setProperty('--dropdown-left', `${calculatedLeft}px`);
					dropdownTarget.style.setProperty('--dropdown-width', `${calculatedWidth}px`);

					// Set inline styles directly as well (for immediate application)
					// This ensures positioning works even if CSS variable isn't read correctly
					dropdownTarget.style.setProperty('top', `${calculatedTop}px`, 'important');
					dropdownTarget.style.setProperty('left', `${calculatedLeft}px`, 'important');
					dropdownTarget.style.setProperty('width', `${calculatedWidth}px`, 'important');
					dropdownTarget.style.setProperty('position', 'fixed', 'important');
				}
			} else {
				// Hide dropdown if Technical section is collapsed or element not visible
				dropdownTarget.style.setProperty('--dropdown-top', '-9999px');
				dropdownTarget.style.setProperty('top', '-9999px', 'important');
			}
		};

		// Throttle position updates to ~60fps (16ms) for better performance
		const throttledUpdatePosition = _throttle(updatePosition, 16, {
			leading: true,
			trailing: true,
		});

		// Initial positioning - try immediately first, then refine after DOM settles
		// This ensures dropdown is positioned as soon as possible when it opens
		let rafId2: number;
		let rafId3: number;

		// Try positioning immediately (in case DOM is already ready)
		updatePosition();

		// Then run again after DOM fully settles (double RAF ensures React has rendered)
		const rafId1 = requestAnimationFrame(() => {
			rafId2 = requestAnimationFrame(() => {
				updatePosition(); // Refine position after DOM is fully rendered

				// Run one more time after content has loaded (text nodes rendered)
				// This ensures positioning is correct even after content loads
				rafId3 = requestAnimationFrame(() => {
					updatePosition(); // Final position after all content is rendered
				});
			});
		});

		// Listen to sidebar content scroll (for when Technical section scrolls)
		const sidebarContent = sidebar?.querySelector('.sidebar__content');
		if (sidebarContent) {
			sidebarContent.addEventListener('scroll', throttledUpdatePosition, true);
		}

		// Also listen to window scroll since dropdown is fixed positioned
		window.addEventListener('scroll', throttledUpdatePosition, true);
		window.addEventListener('resize', throttledUpdatePosition);

		// Use ResizeObserver instead of MutationObserver for better performance
		// This watches for size changes on the toggle container
		const resizeObserver = new ResizeObserver(throttledUpdatePosition);
		if (toggleContainer) {
			resizeObserver.observe(toggleContainer);
		}

		// Also observe the dropdown content itself to recalculate when content loads/changes
		if (dropdownTarget) {
			const dropdownContent = dropdownTarget.querySelector('.dropdown__body');
			if (dropdownContent) {
				resizeObserver.observe(dropdownContent);
			}
		}

		return () => {
			// Cancel all RAF calls if they exist
			if (rafId1) {
				cancelAnimationFrame(rafId1);
			}

			if (rafId2) {
				cancelAnimationFrame(rafId2);
			}

			if (rafId3) {
				cancelAnimationFrame(rafId3);
			}

			// Cancel throttled function
			throttledUpdatePosition.cancel();
			if (sidebarContent) {
				sidebarContent.removeEventListener('scroll', throttledUpdatePosition, true);
			}

			window.removeEventListener('scroll', throttledUpdatePosition, true);
			window.removeEventListener('resize', throttledUpdatePosition);
			resizeObserver.disconnect();

			// Clean up inline styles when effect unmounts or navigation changes
			// This ensures old navigation works when switching back
			// Use dropdownTarget variable captured at start of effect
			if (dropdownTarget) {
				dropdownTarget.style.removeProperty('top');
				dropdownTarget.style.removeProperty('left');
				dropdownTarget.style.removeProperty('width');
				dropdownTarget.style.removeProperty('position');
				dropdownTarget.style.removeProperty('max-width');
				dropdownTarget.style.removeProperty('right');
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isNewNavigation, isWebsiteSwitcherOpen]);
}
